import React, { useMemo, useState, useCallback } from "react";

import { FormBuilder, IFileUploadValue } from "shared/components";
import { IFormButtons } from "shared/types";
import { IFile } from "shared/types";
import { getFormFields } from "./formData";

interface FileFormProps {
  isSubmitting?: boolean;
  hasError?: boolean;
  defaultValues?: Record<string, any>;
  buttons?: IFormButtons;
  submitMode?: "isDirty";
  resetForm?: boolean;
  inlineForm?: boolean;
  handleSubmit: (file: Partial<IFile>) => void;
}

export const FileForm: React.FC<FileFormProps> = ({
  resetForm,
  handleSubmit,
  hasError,
  defaultValues,
  ...rest
}) => {
  const cleanDefaultValues = useMemo(
    () =>
      defaultValues
        ? {
            ...defaultValues,
            file: { originalURL: defaultValues?.source },
          }
        : undefined,
    [defaultValues]
  );

  const [forceUpdateValues, setForceUpdateValues] = useState<Partial<IFile> | undefined>();
  const [canForceUpdate, setCanForceUpdate] = useState(true);

  const handleCleanSubmit = ({
    title,
    description,
    file,
  }: {
    title: string;
    description?: string;
    file: IFileUploadValue;
  }) => {
    const cleanValue: Partial<IFile> = {
      title,
      description,
      url: file.file?.url,
    };

    handleSubmit(cleanValue);
  };

  const onFileChange = useCallback(
    (file: File) => {
      if (canForceUpdate) {
        setForceUpdateValues({
          title: file.name,
        });
      }
    },
    [canForceUpdate, setForceUpdateValues]
  );

  const onTitleChange = useCallback(
    (title: string) => {
      setCanForceUpdate(!title.length);
    },
    [setCanForceUpdate]
  );

  const fields = useMemo(
    () =>
      getFormFields({
        onTitleChange,
        onFileChange,
      }),
    [onTitleChange, onFileChange]
  );

  return (
    <FormBuilder
      fields={fields}
      onSubmit={handleCleanSubmit}
      defaultValues={cleanDefaultValues}
      forceUpdateValues={forceUpdateValues}
      globalError
      submitMode="isDirty"
      resetForm={resetForm}
      apiErrorMessage={hasError}
      {...rest}
    />
  );
};
